import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  BatchUploadRequest,
  BatchMetadataUploadResponse,
  BatchMetadataHistory,
  InputDatasourceManifest,
} from '@/app/shared/types/models/batch-metadata.interface';
import batchService, {
  BatchMetadataProgressEvent,
} from './batch-service';

export const useBatchStore = defineStore('batch', () => {
  const metadataHistory = ref<BatchMetadataHistory[]>([]);
  const inputSources = ref<string[]>([]);
  const inputManifest = ref<InputDatasourceManifest | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch batch metadata history from backend (cached from S3)
   */
  async function fetchMetadataHistory(): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await batchService.getMetadataHistory();
      metadataHistory.value = response.data.data || [];
    } catch (err: any) {
      error.value =
        err.response?.data?.message || 'Failed to fetch metadata history';
      console.error('Error fetching metadata history:', err);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Upload batch metadata to S3
   */
  async function uploadMetadata(
    request: BatchUploadRequest
  ): Promise<BatchMetadataUploadResponse> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await batchService.uploadMetadata(request);
      const uploadResponse: BatchMetadataUploadResponse = response.data.data;

      // Refresh the history after successful upload
      // Note: The uploaded file won't appear immediately since it needs to be processed
      // and moved to the CompletedExecutions folder by the Lambda
      await fetchMetadataHistory();

      return uploadResponse;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to upload metadata';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Upload batch metadata to S3 with streaming progress events.
   *
   * Resolves once the server emits its `complete` event, after refreshing
   * the history (so the caller can return to the history tab and see any
   * already-completed jobs immediately). The Lambda still runs asynchronously
   * after the upload completes, so newly-submitted jobs will appear in the
   * history list only after Lambda processing finishes.
   */
  async function streamUploadMetadata(
    request: BatchUploadRequest,
    onProgress?: (_event: BatchMetadataProgressEvent) => void
  ): Promise<BatchMetadataUploadResponse> {
    isLoading.value = true;
    error.value = null;

    try {
      const uploadResponse = await batchService.streamUploadMetadata(
        request,
        onProgress
      );
      await fetchMetadataHistory();
      return uploadResponse;
    } catch (err: any) {
      error.value = err.message || 'Failed to upload metadata';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchInputSources(): Promise<void> {
    try {
      const response = await batchService.getInputSources();
      inputSources.value = response.data.data || [];
    } catch (err: any) {
      console.error('Error fetching input sources:', err);
    }
  }

  /**
   * Fetch the input datasource manifest that maps each datasource to the
   * input files available for it.
   */
  async function fetchInputManifest(): Promise<void> {
    try {
      const response = await batchService.getInputManifest();
      inputManifest.value = response.data.data || null;
    } catch (err: any) {
      console.error('Error fetching input datasource manifest:', err);
      inputManifest.value = null;
    }
  }

  return {
    metadataHistory,
    inputSources,
    inputManifest,
    isLoading,
    error,
    fetchMetadataHistory,
    fetchInputSources,
    fetchInputManifest,
    uploadMetadata,
    streamUploadMetadata,
  };
});
